import { AttestationDay, AttestationStatus } from "../../../types/attestation";

/**
 * Calendar key for a day-strip date. Uses the same UTC accessors the strip uses to render
 * its day number (`getUTCDate()`), so a marker always sits under the number it describes.
 */
export const toDayStripKey = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Attestation status for a day-strip date, or undefined when it is not a day requiring attestation.
 *
 * Deliberately searches the whole list rather than the current pay period. Every day on screen
 * that is still owed carries its marker, including one left behind by a period that has already
 * closed — the BT needs to see it is waiting on them. Only the counts and the catch-up run are
 * scoped to the period; see `daysInCurrentPayPeriod`.
 */
export const findAttestationDay = (
  date: Date,
  attestationDays: AttestationDay[]
): AttestationDay | undefined => {
  const key = toDayStripKey(date);

  return attestationDays.find((candidate) => candidate.date === key);
};

/** A usable YYYY-MM-DD key. Anything else cannot be placed against the pay-period window. */
const isDateKey = (value?: string): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * Whether the response says enough about the pay period to tell its days from the rest.
 *
 * Only the start matters. A malformed end date costs the banner its deadline sentence and
 * nothing more, but without a start there is no boundary at all — and since the response now
 * covers whatever week is on screen as well as the period, treating every day in it as the
 * period's would be exactly the overstatement this scoping exists to prevent.
 */
export const canScopeToPayPeriod = (
  status: AttestationStatus | null | undefined
): boolean => isDateKey(status?.payPeriod?.startDate);

/**
 * The days that belong to the pay period the BT is being asked to close out.
 *
 * The status also carries the days of the week the calendar is showing, which routinely reaches
 * outside the period — and days a closed period left owed. Those stay visible on the strip, and
 * a BT who opens one can still confirm it, but they are kept out of every count and out of the
 * catch-up run: mixing them in overstated what this period needs, attached days to a deadline
 * that had already passed, and gave the run days it could never legitimately finish.
 *
 * Comparing the keys as strings is exact: both sides are zero-padded YYYY-MM-DD, so lexical and
 * chronological order are the same thing, with no timezone to get wrong.
 */
export const daysInCurrentPayPeriod = (
  status: AttestationStatus | null | undefined
): AttestationDay[] => {
  const days = status?.days;
  const { startDate, endDate } = status?.payPeriod ?? {};

  if (!Array.isArray(days) || !isDateKey(startDate)) {
    return [];
  }

  return days.filter(
    (day) =>
      isDateKey(day.date) &&
      day.date >= startDate &&
      // An unusable end date is no upper bound, rather than a bound of nothing: the period's own
      // days are still the ones on or after its start.
      (!isDateKey(endDate) || day.date <= endDate)
  );
};
